import React from 'react';
import { useTranslation } from 'react-i18next';

import openPopupOperation from 'editor/src/store/app/module/popup/operation/openPopupOperation';
import setDesignDataOperation from 'editor/src/store/design/operation/setDesignDataOperation';
import setSidebarActiveTabByNameOperation from 'editor/src/store/editorModules/sidebar/operation/setSidebarActiveTabByNameOperation';
import { TAB_NAMES } from 'editor/src/store/editorModules/sidebar/types';
import { addUploadedFontsAction } from 'editor/src/store/fonts/slice';
import addUploadedImagesOperation from 'editor/src/store/gallery/operation/addUploadedImagesOperation';
import { UploadedImageData } from 'editor/src/store/gallery/types';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import toastController from 'editor/src/util/toastController';

import { PopupName } from 'editor/src/component/Popup/popups';

import importInDesignApi, { ImportInDesignGalleryImage, ImportInDesignWarning } from './importInDesignApi';

import importInDesignConfirmationStyles from './importInDesignConfirmation.module.scss';

type FitMode = 'fit_inside' | 'fill_available_area';

const MAX_INDESIGN_IMPORT_FILE_SIZE_BYTES = 100 * 1024 * 1024;
const FONT_FILE_EXTENSIONS = /\.(otf|ttf|woff2?)/i;

function hasFontReferences(designData: object): boolean {
  const json = JSON.stringify(designData);
  return FONT_FILE_EXTENSIONS.test(json);
}

function mimeTypeFromAssetType(type: string): string {
  switch (type.toUpperCase()) {
    case 'PNG':
      return 'image/png';
    case 'JPG':
    case 'JPEG':
      return 'image/jpeg';
    case 'WEBP':
      return 'image/webp';
    case 'PDF':
      return 'application/pdf';
    case 'SVG':
      return 'image/svg+xml';
    default:
      return type;
  }
}

function mapImportedGalleryImages(images: ImportInDesignGalleryImage[]): UploadedImageData[] {
  return images.map((image) => ({
    file: {
      id: image.id,
      name: image.name,
      type: mimeTypeFromAssetType(image.type),
      width: image.width,
      height: image.height,
      source: 'indesign-import',
    },
    url: image.url,
    thumbnailUrl: image.thumbnailUrl,
    mimeType: mimeTypeFromAssetType(image.type),
  }));
}

function formatWarnings(warnings: ImportInDesignWarning[]): string {
  return warnings.map((warning) => warning.message).join('\n');
}

export default function useImportInDesign() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const pendingFileRef = React.useRef<File | null>(null);
  const [isImporting, setIsImporting] = React.useState(false);
  const productUid = useSelector((state) => state.design.designData?.product_uid);
  const inDesignImportClientId = useSelector((state) => state.hostSettings.inDesignImportClientId);

  const submitImport = React.useCallback(
    async (file: File, fitMode?: FitMode) => {
      if (!productUid) {
        toastController.error(t('editor-indesign-import-failed'), t('editor-indesign-no-product-loaded'));
        return;
      }

      if (file.size > MAX_INDESIGN_IMPORT_FILE_SIZE_BYTES) {
        toastController.error(
          t('editor-indesign-import-failed'),
          t('editor-indesign-file-too-large', { defaultValue: 'File exceeds 100MB limit' }),
        );
        return;
      }

      setIsImporting(true);
      try {
        const result = await importInDesignApi({ file, productUid, fitMode, clientId: inDesignImportClientId });

        if (result.status === 'needs_fit_mode') {
          pendingFileRef.current = file;
          setIsImporting(false);
          const fitOptionTitles: Record<string, string> = {
            fit_inside: t('editor-indesign-keep-full-design'),
            fill_available_area: t('editor-indesign-fill-available-area'),
          };
          dispatch(
            openPopupOperation(PopupName.LOCAL_CONFIRMATION_POPUP, {
              popupTitle: t('editor-indesign-choose-fit'),
              textLines: [t('editor-indesign-proportions-mismatch')],
              options: result.fitOptions.map((fitOption) => ({
                title: fitOptionTitles[fitOption] ?? fitOption,
                onConfirm: () => {
                  if (pendingFileRef.current) {
                    void submitImport(pendingFileRef.current, fitOption as FitMode);
                  }
                },
              })),
              hideTitleIcon: true,
            }),
          );
          return;
        }

        if (result.status === 'error') {
          throw new Error(result.errors.map((error) => error.message).join('\n') || t('editor-indesign-import-failed'));
        }

        pendingFileRef.current = null;
        if (result.fonts?.length) {
          dispatch(addUploadedFontsAction(result.fonts));
        } else if (result.fonts !== undefined && result.fonts.length === 0 && hasFontReferences(result.designData)) {
          toastController.warning(
            t('editor-indesign-import-fonts-missing'),
            t('editor-indesign-fonts-could-not-be-loaded'),
          );
        }
        if (result.galleryImages?.length) {
          await dispatch(addUploadedImagesOperation(mapImportedGalleryImages(result.galleryImages)));
        }
        await dispatch(setDesignDataOperation(result.designData));
        dispatch(setSidebarActiveTabByNameOperation(TAB_NAMES.LAYERS));
        toastController.success(t('editor-indesign-import-complete'), t('editor-indesign-imported-successfully'));
        if (result.warnings.length) {
          toastController.infoPersist(t('editor-indesign-import-warnings'), formatWarnings(result.warnings));
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : t('editor-indesign-unable-to-import');
        toastController.error(t('editor-indesign-import-failed'), message);
      } finally {
        setIsImporting(false);
      }
    },
    [dispatch, productUid, inDesignImportClientId, t],
  );

  const onFileChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) {
        return;
      }
      pendingFileRef.current = file;
      void submitImport(file);
    },
    [submitImport],
  );

  const openFilePicker = React.useCallback(() => {
    inputRef.current?.click();
  }, []);

  const openImportConfirmation = React.useCallback(() => {
    dispatch(
      openPopupOperation(PopupName.LOCAL_CONFIRMATION_POPUP, {
        popupTitle: t('editor-indesign-import-confirmation'),
        textLines: [t('editor-indesign-will-replace-design')],
        options: [
          { title: t('editor-cancel') },
          {
            title: t('Continue'),
            onConfirm: openFilePicker,
          },
        ],
        hideTitleIcon: true,
        className: importInDesignConfirmationStyles.inlineButtonsConfirmation,
      }),
    );
  }, [dispatch, openFilePicker, t]);

  const fileInput = <input ref={inputRef} hidden type="file" accept=".zip,application/zip" onChange={onFileChange} />;

  return {
    fileInput,
    isImporting,
    openImportConfirmation,
  };
}
